import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, RefreshCw, Eye, X as XIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UnappliedSplitsBanner — Feb 2026 (v2: reverse-split aware +
 * before/after preview modal).
 *
 * Yellow warning banner shown on the Tax Lots / Year End / Account
 * Audit pages when a stock the user holds has had a confirmed split
 * (per stock_splits_upcoming.execution_date <= today) that hasn't
 * been applied to their lots yet. Backend returns `is_reverse=true`
 * when ratio < 1 so we swap "forward split" copy for "reverse split"
 * copy automatically. Preview modal shows every affected lot's
 * before/after so the user can eyeball the ratio math before
 * committing.
 */
export default function UnappliedSplitsBanner({ portfolioId = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = portfolioId
        ? `${API}/portfolio/unapplied-splits?portfolio_id=${portfolioId}`
        : `${API}/portfolio/unapplied-splits`;
      const { data } = await axios.get(url, { withCredentials: true });
      setRows(data?.unapplied || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [portfolioId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyNow = async () => {
    if (applying) return;
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API}/portfolio/unapplied-splits/apply`,
        {},
        { withCredentials: true },
      );
      toast.success('Adjustments applied', {
        description: `${data.lots_modified} lot${data.lots_modified === 1 ? '' : 's'} adjusted across ${data.splits_processed} split${data.splits_processed === 1 ? '' : 's'}.`,
      });
      setPreviewOpen(false);
      await load();
    } catch (err) {
      toast.error('Failed to apply', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setApplying(false);
    }
  };

  if (loading || rows.length === 0) return null;

  // Reverse-split aware headline copy. If EVERY row is a reverse
  // split, say so; if mixed, use the generic "adjustments" wording.
  const allReverse = rows.every((r) => r.is_reverse);
  const allForward = rows.every((r) => !r.is_reverse);
  const kindLabel = allReverse ? 'reverse split' : (allForward ? 'stock split' : 'split adjustment');
  const kindLabelPl = allReverse ? 'reverse splits' : (allForward ? 'stock splits' : 'split adjustments');

  return (
    <>
      <div
        className="rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 mb-4"
        data-testid="unapplied-splits-banner"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-amber-900 dark:text-amber-100">
              {rows.length} unapplied {rows.length === 1 ? kindLabel : kindLabelPl} on your holdings
            </div>
            <div className="text-xs text-amber-800 dark:text-amber-200 mt-1 leading-relaxed">
              The cost basis and share counts on this page do <strong>not</strong> yet
              reflect {rows.length === 1 ? 'this' : 'these'}. Apply so your tax numbers
              stay accurate. The nightly auto-applier runs at 3 AM ET; preview then
              apply immediately below.
            </div>
            <ul className="mt-2 space-y-0.5 text-[11px] font-mono">
              {rows.slice(0, 10).map((r) => (
                <li key={`${r.ticker}-${r.execution_date}`} className="text-amber-900 dark:text-amber-100">
                  <span className="font-bold">{r.ticker}</span>
                  <span className="text-amber-700 dark:text-amber-300">
                    {' · '}{r.ratio_label} {r.is_reverse ? 'reverse split' : 'split'} on {r.execution_date}
                  </span>
                  <span className="text-amber-600 dark:text-amber-400"> · {r.affected_lots} lot{r.affected_lots === 1 ? '' : 's'} affected</span>
                </li>
              ))}
              {rows.length > 10 && (
                <li className="text-amber-700 dark:text-amber-300 italic">…and {rows.length - 10} more</li>
              )}
            </ul>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white transition-colors"
                data-testid="unapplied-splits-preview-btn"
              >
                <Eye className="w-3.5 h-3.5" /> Preview &amp; apply
              </button>
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="text-[11px] text-amber-700 dark:text-amber-300 hover:underline"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {previewOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
          onClick={() => setPreviewOpen(false)}
          data-testid="unapplied-splits-preview-modal"
        >
          <div
            className="bg-card border rounded-xl shadow-2xl w-full max-w-3xl relative max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Eye className="w-4 h-4 text-amber-500" />
                Split Preview — before / after
              </h3>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="p-1 rounded hover:bg-muted text-muted-foreground"
                data-testid="unapplied-splits-preview-close"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-4">
              <p className="text-xs text-muted-foreground">
                Total cost per lot should stay identical after the adjustment
                (a split reshapes shares × cost-per-share, never total$).
                Eyeball each row below — if any total shifts by more than a
                cent (rounding), stop and ping the admin.
              </p>
              {rows.map((r) => (
                <div key={`${r.ticker}-${r.execution_date}`} className="rounded-md border border-border/60 bg-muted/20 p-2">
                  <div className="text-sm font-bold flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{r.ticker}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      r.is_reverse
                        ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200 ring-1 ring-rose-300/50'
                        : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 ring-1 ring-emerald-300/50'
                    }`}>
                      {r.ratio_label} {r.is_reverse ? 'Reverse Split' : 'Forward Split'}
                    </span>
                    <span className="text-[11px] font-mono text-muted-foreground">{r.execution_date}</span>
                  </div>
                  <div className="mt-1.5 overflow-x-auto">
                    <table className="w-full text-[10px] font-mono">
                      <thead>
                        <tr className="text-muted-foreground uppercase tracking-wider text-[9px]">
                          <th className="text-left py-1 pr-2">Purchased</th>
                          <th className="text-right py-1 px-2">Before Shares</th>
                          <th className="text-right py-1 px-2">Before $/sh</th>
                          <th className="text-right py-1 px-2">Before Total</th>
                          <th className="text-right py-1 px-2">→</th>
                          <th className="text-right py-1 px-2">After Shares</th>
                          <th className="text-right py-1 px-2">After $/sh</th>
                          <th className="text-right py-1 pl-2">After Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(r.lot_previews || []).map((L) => {
                          const drift = Math.abs((L.after_total_cost || 0) - (L.before_total_cost || 0));
                          const driftBad = drift > 0.01;
                          return (
                            <tr key={L.position_id} className="border-t border-border/40">
                              <td className="py-1 pr-2">{L.date_purchased}</td>
                              <td className="py-1 px-2 text-right">{L.before_shares}</td>
                              <td className="py-1 px-2 text-right">${L.before_cost_per_share.toFixed(4)}</td>
                              <td className="py-1 px-2 text-right">${L.before_total_cost.toFixed(2)}</td>
                              <td className="py-1 px-2 text-right text-muted-foreground">→</td>
                              <td className="py-1 px-2 text-right font-bold">{L.after_shares}</td>
                              <td className="py-1 px-2 text-right font-bold">${L.after_cost_per_share.toFixed(4)}</td>
                              <td className={`py-1 pl-2 text-right font-bold ${driftBad ? 'text-rose-600 dark:text-rose-400' : ''}`}
                                  title={driftBad ? `Drift $${drift.toFixed(4)} — investigate before applying` : undefined}>
                                ${L.after_total_cost.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t bg-muted/30">
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-background border border-border hover:bg-muted"
                data-testid="unapplied-splits-preview-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyNow}
                disabled={applying}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                data-testid="unapplied-splits-preview-confirm"
              >
                {applying
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying…</>
                  : <><RefreshCw className="w-3.5 h-3.5" /> Confirm &amp; apply {rows.length} adjustment{rows.length === 1 ? '' : 's'}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
